'use client'

import { Audio } from '@/components/ui/audio'
import { motion } from 'framer-motion'
import { forwardRef } from 'react'

interface IAudioPlayerCardProps {
    fileUrl: string
    fileName: string
    fileType: string | null
    onCanPlay: () => void
    onStopPropagation: (e: React.MouseEvent) => void
    onPlay: (e: React.MouseEvent<HTMLAudioElement>) => void
}

export const AudioPlayerCard = forwardRef<HTMLAudioElement, IAudioPlayerCardProps>(
    ({ fileUrl, fileName, fileType, onCanPlay, onStopPropagation, onPlay }, ref) => {
        const fileNameWithoutFileExtension = fileName.replace(/\.[^/.]+$/, '')

        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 flex w-full h-full min-h-full items-baseline justify-center"
                onClick={onStopPropagation}
            >
                <div className="flex flex-col items-center justify-center min-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="w-full aspect-square bg-linear-to-br from-purple-600 via-indigo-500 to-blue-500 relative overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="relative">
                                <div className="absolute -inset-8 bg-white/20 rounded-full blur-xl animate-pulse"></div>
                                <div className="rounded-full p-8">
                                    <span className="text-[120px] text-white drop-shadow-lg">🎵</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="w-full px-6 pt-6 pb-7 bg-white">
                        <h3 className="text-xl font-medium text-gray-900 truncate" title={fileNameWithoutFileExtension}>
                            {fileNameWithoutFileExtension}
                        </h3>
                        <p className="text-sm text-gray-500 mb-5">{fileType || 'Audio Track'}</p>
                        <Audio
                            ref={ref}
                            src={fileUrl}
                            controls
                            onCanPlay={onCanPlay}
                            onPlay={onPlay}
                            className="w-full outline-none focus:outline-none"
                            onClick={onStopPropagation}
                        />
                    </div>
                </div>
            </motion.div>
        )
    }
)

AudioPlayerCard.displayName = 'AudioPlayerCard'
